import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// ------- Gate 6 · Slice 6.3 preview · Insights snapshot ------------------

function freshnessTone(lastPulledAt) {
  if (!lastPulledAt) return "muted";
  const hours = (Date.now() - new Date(lastPulledAt).getTime()) / 3_600_000;
  if (hours <= 8) return "success";
  if (hours <= 24) return "warn";
  return "danger";
}

function InsightsSnapshotCard({ freshness }) {
  if (!freshness) return null;
  const meta = (freshness.sources || []).find((s) => s.source === "meta_graph_insights") || {};
  const total = freshness.total_receipts_with_external_id || 0;
  const covered = freshness.total_receipts_with_any_attribution || 0;
  const pct = total > 0 ? Math.round((covered / total) * 100) : null;
  return (
    <section className="bo-card" data-testid="operate-insights-snapshot" style={{ marginTop: 24 }}>
      <header className="bo-card-header">
        <h3>Insights snapshot</h3>
        <Link to="/brand-os/insights" className="bo-link-sm">Open Insights →</Link>
      </header>
      <div className="bo-metric-row" data-testid="operate-insights-metrics">
        <div className="bo-metric">
          <div className="bo-metric-label">Meta freshness</div>
          <div className="bo-metric-value">
            <Pill tone={freshnessTone(meta.last_pulled_at)}>
              {meta.last_pulled_at ? timeAgo(meta.last_pulled_at) : "Never"}
            </Pill>
          </div>
        </div>
        <div className="bo-metric">
          <div className="bo-metric-label">Attribution rows</div>
          <div className="bo-metric-value" data-testid="operate-insights-rows">
            {meta.rows_total ?? 0}
          </div>
        </div>
        <div className="bo-metric">
          <div className="bo-metric-label">Receipts covered</div>
          <div className="bo-metric-value" data-testid="operate-insights-coverage">
            {covered}<span className="bo-metric-of">/{total}</span>
            {pct !== null && <span style={{ marginLeft: 6, fontSize: 13, color: "var(--bo-muted)" }}>({pct}%)</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

const HEALTH_TONE = {
  HEALTHY: "success",
  ACTION_REQUIRED: "warn",
  UNHEALTHY: "danger",
  UNKNOWN: "muted",
};

const CHANNEL_LABEL = {
  facebook: "Facebook",
  email: "Email",
  manual_export: "Manual Export",
};

const STATE_LABEL = {
  scheduled: "Scheduled",
  paused_unhealthy_connection: "Paused",
  processing: "Processing",
  published: "Published",
  failed: "Failed",
  canceled: "Canceled",
  missed: "Missed",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function OperateOverview() {
  const [connections, setConnections] = useState(null);
  const [schedules, setSchedules] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, s, r, ss, f] = await Promise.all([
          api.get("/publish/channel-connections"),
          api.get("/publish-schedules?limit=6"),
          api.get("/publish-receipts?limit=5"),
          api.get("/publish-scheduler/status").catch(() => ({ data: null })),
          api.get("/attributions/freshness").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setConnections(c.data?.connections || c.data?.channel_connections || []);
        setSchedules(s.data?.schedules || s.data?.publish_schedules || []);
        setReceipts(r.data?.receipts || r.data?.publish_receipts || []);
        setScheduler(ss?.data || null);
        setFreshness(f?.data || null);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  const healthySummary = useMemo(() => {
    if (!connections) return null;
    const healthy = connections.filter((c) => c.health === "HEALTHY").length;
    const total = connections.length;
    return { healthy, total, needsAttention: total - healthy };
  }, [connections]);

  const upcoming = useMemo(() => {
    if (!schedules) return [];
    return schedules
      .filter((s) => s.state === "scheduled")
      .sort((a, b) => (a.scheduled_for || "").localeCompare(b.scheduled_for || ""))
      .slice(0, 3);
  }, [schedules]);

  const paused = useMemo(() => {
    if (!schedules) return [];
    return schedules.filter((s) => s.state === "paused_unhealthy_connection").slice(0, 3);
  }, [schedules]);

  return (
    <Shell>
      <div className="bo-page" data-testid="operate-overview-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">OPERATE</div>
            <h1 data-testid="operate-overview-title">Operations at a glance</h1>
            <p className="bo-page-lede">
              Queue, calendar, connection health, and the last few things that went out — one screen.
            </p>
          </div>
          <div className="bo-page-actions">
            <Link
              to="/brand-os/publish/new"
              className="bo-btn bo-btn-primary"
              data-testid="operate-new-publish-cta"
            >
              New Publish
            </Link>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="operate-overview-error" />}

        <div className="bo-grid-3" data-testid="operate-overview-cards">
          {/* Card 1: Connection health */}
          <section className="bo-card" data-testid="operate-connection-health-card">
            <header className="bo-card-header">
              <h3>Connection health</h3>
              <Link to="/brand-os/admin/channel-connections" className="bo-link-sm">Manage →</Link>
            </header>
            {!connections ? (
              <LoadingCard />
            ) : healthySummary.total === 0 ? (
              <EmptyState
                testId="operate-no-connections"
                title="No channels connected yet"
                description="Connect Facebook or another channel to start scheduling."
              />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Healthy</div>
                    <div className="bo-metric-value" data-testid="operate-healthy-count">
                      {healthySummary.healthy}<span className="bo-metric-of">/{healthySummary.total}</span>
                    </div>
                  </div>
                  {healthySummary.needsAttention > 0 && (
                    <div className="bo-metric">
                      <div className="bo-metric-label">Attention</div>
                      <div className="bo-metric-value warn">
                        {healthySummary.needsAttention}
                      </div>
                    </div>
                  )}
                </div>
                <ul className="bo-list-tight">
                  {connections.slice(0, 4).map((c) => (
                    <li key={c.id} data-testid={`operate-conn-${c.id}`}>
                      <span className="bo-conn-name">
                        {CHANNEL_LABEL[c.channel] || c.channel}
                        {c.provider_ref?.page_name && ` · ${c.provider_ref.page_name}`}
                      </span>
                      <Pill tone={HEALTH_TONE[c.health] || "muted"}>{c.health}</Pill>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* Card 2: Publish Queue snapshot */}
          <section className="bo-card" data-testid="operate-queue-card">
            <header className="bo-card-header">
              <h3>Upcoming publishes</h3>
              <Link to="/brand-os/publish/queue" className="bo-link-sm">See queue →</Link>
            </header>
            {!schedules ? (
              <LoadingCard />
            ) : upcoming.length === 0 && paused.length === 0 ? (
              <EmptyState
                testId="operate-queue-empty"
                title="Queue is empty"
                description="Schedule a post from New Publish to see it here."
              />
            ) : (
              <>
                {upcoming.length > 0 && (
                  <ul className="bo-list-tight" data-testid="operate-queue-upcoming">
                    {upcoming.map((s) => (
                      <li key={s.id}>
                        <div>
                          <div className="bo-list-title">
                            {CHANNEL_LABEL[s.channel] || s.channel}
                          </div>
                          <div className="bo-list-sub">{formatWhen(s.scheduled_for)}</div>
                        </div>
                        <Pill tone="muted">{STATE_LABEL[s.state] || s.state}</Pill>
                      </li>
                    ))}
                  </ul>
                )}
                {paused.length > 0 && (
                  <div style={{ marginTop: 10 }} data-testid="operate-queue-paused">
                    <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Paused (connection unhealthy)</div>
                    <ul className="bo-list-tight">
                      {paused.map((s) => (
                        <li key={s.id}>
                          <span>{CHANNEL_LABEL[s.channel] || s.channel}</span>
                          <Pill tone="warn">Paused</Pill>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Card 3: Recent publishes */}
          <section className="bo-card" data-testid="operate-receipts-card">
            <header className="bo-card-header">
              <h3>Recent publishes</h3>
              <Link to="/brand-os/publish/queue" className="bo-link-sm">See all →</Link>
            </header>
            {!receipts ? (
              <LoadingCard />
            ) : receipts.length === 0 ? (
              <EmptyState
                testId="operate-receipts-empty"
                title="No publishes yet"
                description="After you Publish Now from Compose, receipts show up here."
              />
            ) : (
              <ul className="bo-list-tight" data-testid="operate-receipts-list">
                {receipts.slice(0, 5).map((r) => (
                  <li key={r.id}>
                    <div>
                      <div className="bo-list-title">
                        {CHANNEL_LABEL[r.channel] || r.channel}
                      </div>
                      <div className="bo-list-sub">{timeAgo(r.published_at)}</div>
                    </div>
                    <Pill tone={r.execution_status === "published" ? "success" : "danger"}>
                      {r.execution_status}
                    </Pill>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Scheduler heartbeat */}
        <section className="bo-card" style={{ marginTop: 24 }} data-testid="operate-scheduler-card">
          <header className="bo-card-header">
            <h3>Scheduler heartbeat</h3>
          </header>
          {!scheduler ? (
            <div className="bo-empty-inline">Scheduler status unavailable.</div>
          ) : (
            <div className="bo-metric-row" data-testid="operate-scheduler-metrics">
              <div className="bo-metric">
                <div className="bo-metric-label">Running</div>
                <div className="bo-metric-value">
                  <Pill tone={scheduler.running ? "success" : "danger"}>
                    {scheduler.running ? "yes" : "no"}
                  </Pill>
                </div>
              </div>
              <div className="bo-metric">
                <div className="bo-metric-label">Last tick</div>
                <div className="bo-metric-value">{timeAgo(scheduler.last_tick_at)}</div>
              </div>
              {typeof scheduler.processed_last_tick === "number" && (
                <div className="bo-metric">
                  <div className="bo-metric-label">Processed / tick</div>
                  <div className="bo-metric-value">{scheduler.processed_last_tick}</div>
                </div>
              )}
            </div>
          )}
        </section>

        <InsightsSnapshotCard freshness={freshness} />
      </div>
    </Shell>
  );
}
